// Device provisioning (spec §8.2; Gate Check-In Spec §2).
//
// Opened once, online, from the link the organizer produced in the dashboard.
// It binds the device's long-lived credential and pulls the offline dataset.
import { provision as provisionRequest } from './api.js';
import { setMeta } from './db.js';
import { storeDataset } from './sync.js';

/**
 * The token arrives in the URL FRAGMENT, never the query string.
 *
 * A fragment is not transmitted to a server, so the credential stays out of
 * access logs, out of Referer headers and out of every proxy in between.
 */
export function tokenFromLocation() {
  const hash = window.location.hash;
  if (!hash || hash.length < 2) return null;
  return tokenFromText(hash);
}

/**
 * Read a token out of pasted text — either a whole setup link or the bare
 * token.
 *
 * This exists because of iOS. A home screen web app there has its own storage,
 * separate from Safari's, and tapping a link opens Safari rather than the
 * installed app. So the provisioning link can only ever reach Safari's copy,
 * while the copy that must hold the credential is the installed one — which
 * has no address bar to navigate. Pasting is the only way in.
 *
 * Accepting the bare token as well as the full link is deliberate: a volunteer
 * copying from a phone screen very often selects only part of a long URL, and
 * the fragment is the tail, so a partial copy is more likely to be the token
 * alone than a truncated link.
 */
export function tokenFromText(text) {
  const trimmed = (text ?? '').trim();
  if (!trimmed) return null;
  const hash = trimmed.indexOf('#');
  const raw = hash === -1 ? trimmed : trimmed.slice(hash + 1);
  if (!raw) return null;
  try {
    return decodeURIComponent(raw) || null;
  } catch {
    // A malformed escape (`#%`) throws URIError. That is not a token, and it
    // must not take the whole app down before anything renders — a device
    // holding a valid credential still has a gate to run.
    return null;
  }
}

/**
 * Remove the token from the address bar once it has been read, so it does not
 * sit in history or survive a screenshot of the tablet.
 */
export function clearTokenFromLocation() {
  history.replaceState(null, '', window.location.pathname + window.location.search);
}

export async function runProvisioning(token) {
  const payload = await provisionRequest(token);

  await setMeta({
    deviceToken: payload.deviceToken,
    deviceId: payload.device.id,
    gateName: payload.device.gateName,
    label: payload.device.label,
  });
  const ticketCount = await storeDataset(payload);

  /**
   * Ask the browser to keep this data through storage pressure.
   *
   * It is a request, not a guarantee — which is exactly why readiness() also
   * detects the loss. A device that sat in a bag for a week and came back
   * empty must refuse to work, not quietly admit everyone.
   */
  if (navigator.storage?.persist) {
    await navigator.storage.persist().catch(() => false);
  }

  return { ticketCount, gateName: payload.device.gateName, label: payload.device.label };
}
